/**
 * Brand Overview Component
 * Main dashboard overview with real stats and top products
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useBrand } from '../../context/BrandContext';
import { TrendingUp, Package, Calendar, Plus, Eye, Heart, Users, Target, Activity } from 'lucide-react';
import ImageWithFallback from './ImageWithFallback';
import { formatPrice } from '../../services/currencyService';

// Helper function to properly capitalize product titles (title case)
const capitalizeTitle = (title) => {
  if (!title) return '';
  
  // Words that should remain lowercase in title case
  const lowerCaseWords = ['a', 'an', 'the', 'and', 'but', 'or', 'for', 'nor', 'on', 'at', 'to', 'from', 'by', 'with', 'as'];
  
  return title
    .toLowerCase()
    .split(' ')
    .map((word, index) => {
      // Always capitalize first word and words not in the lowerCaseWords list
      if (index === 0 || !lowerCaseWords.includes(word)) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
      return word; // Keep lowercase words lowercase
    })
    .join(' ');
};

function BrandOverview() {
  const { authFetch, isAuthenticated, brand } = useBrand();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!isAuthenticated) return;

      try {
        // Fetch both dashboard stats and profile data
        const [dashboardRes, profileRes] = await Promise.all([
          authFetch('/api/brand/dashboard/'),
          authFetch('/api/brand/profile/')
        ]);

        if (dashboardRes.ok) {
          const data = await dashboardRes.json();
          setDashboardData(data);
        } else {
          setError('Failed to load dashboard data');
        }

        if (profileRes.ok) {
          const data = await profileRes.json();
          setProfileData(data);
        }
      } catch (err) {
        setError('Failed to load dashboard data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [isAuthenticated, authFetch]);

  // Real stats from API
  const stats = dashboardData?.stats || {};
  const recentProducts = dashboardData?.recent_products || [];
  const brandInfo = dashboardData?.brand || {};

  // Calculate growth indicators based on data
  const hasProducts = stats.total_products > 0;
  const hasNewThisMonth = stats.products_this_month > 0;

  const hasViews = (stats.total_product_views || 0) > 0;
  const hasSaves = (stats.total_product_saves || 0) > 0;
  const hasBrandViews = (stats.total_brand_views || 0) > 0;
  const hasBrandSaves = (stats.total_brand_saves || 0) > 0;

  const statsData = [
    { 
      label: 'PRODUCT VIEWS', 
      value: (stats.total_product_views || 0).toLocaleString(),
      description: hasViews ? 'Users viewed your products' : 'Views will appear as users browse',
      trend: hasViews ? 'up' : 'neutral', 
      icon: Eye 
    },
    { 
      label: 'SAVE RATE', 
      value: stats.total_product_views > 0 ? 
        `${((stats.total_product_saves / stats.total_product_views) * 100).toFixed(1)}%` : '0%',
      description: hasViews ? 'Purchase intent indicator' : 'Saves show purchase intent',
      trend: hasSaves ? 'up' : 'neutral', 
      icon: Target 
    },
    { 
      label: 'PRODUCT SAVES', 
      value: (stats.total_product_saves || 0).toLocaleString(),
      description: hasSaves ? 'Users saved your products' : 'Saves show purchase intent',
      trend: hasSaves ? 'up' : 'neutral', 
      icon: Heart 
    },
    { 
      label: 'FOLLOW RATE', 
      value: stats.total_brand_views > 0 ? 
        `${((stats.total_brand_saves / stats.total_brand_views) * 100).toFixed(1)}%` : '0%',
      description: hasBrandSaves ? 'Brand appeal score' : 'Share your brand page',
      trend: hasBrandSaves ? 'up' : 'neutral', 
      icon: Users 
    },
  ];

  const catalogStats = [
    { 
      label: 'TOTAL PRODUCTS', 
      value: stats.total_products || 0,
      description: hasProducts ? 'In your catalog' : 'Add your first product',
      icon: Package 
    },
    { 
      label: 'THIS MONTH', 
      value: stats.products_this_month || 0,
      description: hasNewThisMonth ? 'New additions' : 'No new products',
      icon: Calendar 
    },
  ];

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner} />
        <span style={{ color: '#71717a' }}>Loading dashboard...</span>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.label}>D A S H B O A R D</div>
        <p style={styles.subtitle}>
          Welcome back, {profileData?.contact_name || brandInfo.name || 'there'}! 
          Here's how your brand is performing.
        </p>
      </div>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {/* Engagement Stats Grid */}
      <div className="bp-stats-grid" style={styles.statsGrid}>
        {statsData.map((stat) => {
          const Icon = stat.icon;
          const isUp = stat.trend === 'up';
          return (
            <div key={stat.label} style={styles.statCard}>
              <div style={styles.statHeader}>
                <div style={styles.statIcon}>
                  <Icon style={{ width: '20px', height: '20px', color: '#171717' }} />
                </div>
                {isUp && (
                  <div style={styles.statTrend}>
                    <TrendingUp style={{ width: '14px', height: '14px', color: '#22c55e' }} />
                  </div>
                )}
              </div>
              <div style={styles.statValue}>{typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}</div>
              <div style={styles.statLabel}>{stat.label}</div>
              <div style={styles.statDescription}>{stat.description}</div>
            </div>
          );
        })}
      </div>

      {/* Catalog Stats Row */}
      <div className="bp-catalog-stats" style={{ display: 'flex', gap: '16px', marginBottom: '48px', flexWrap: 'wrap' }}>
        {catalogStats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '16px', padding: '20px 24px', background: '#fafafa', borderRadius: '12px' }}>
              <div style={{ width: '44px', height: '44px', border: '1px solid #e5e5e5', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff' }}>
                <Icon style={{ width: '20px', height: '20px', color: '#171717' }} />
              </div>
              <div>
                <div style={{ fontSize: '11px', letterSpacing: '0.1em', color: '#a1a1aa', fontWeight: '500' }}>{stat.label}</div>
                <div style={{ fontSize: '24px', fontWeight: '400', color: '#171717' }}>{stat.value}</div>
              </div>
              <div style={{ marginLeft: 'auto', fontSize: '13px', color: '#71717a' }}>{stat.description}</div>
            </div>
          );
        })}
        {stats.engagement_score > 0 && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '16px', padding: '20px 24px', background: '#fafafa', borderRadius: '12px' }}>
            <div style={{ width: '44px', height: '44px', border: '1px solid #e5e5e5', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff' }}>
              <TrendingUp style={{ width: '20px', height: '20px', color: '#171717' }} />
            </div>
            <div>
              <div style={{ fontSize: '11px', letterSpacing: '0.1em', color: '#a1a1aa', fontWeight: '500' }}>ENGAGEMENT</div>
              <div style={{ fontSize: '24px', fontWeight: '400', color: '#171717' }}>{stats.engagement_score}%</div>
            </div>
            <div style={{ marginLeft: 'auto', fontSize: '13px', color: '#71717a' }}>Overall score</div>
          </div>
        )}
      </div>

      {/* Recent Products Section */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <div>
            <div style={styles.label}>R E C E N T - P R O D U C T S</div>
          </div>
          {recentProducts.length > 0 && (
          <Link to="/brand-portal/collection" style={styles.viewAllLink}>
            View All →
          </Link>
          )}
        </div>
        
        {recentProducts.length > 0 ? (
          <div className="bp-products-grid" style={styles.productsGrid}>
            {recentProducts.map((product) => (
              <Link 
                key={product.id} 
                to={`/brand-portal/products/${product.id}`}
                style={styles.productCard}
              >
                <div style={styles.productImage}>
                  <ImageWithFallback 
                    src={product.image}
                    alt={product.title}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
                <div style={styles.productInfo}>
                  <h3 style={styles.productTitle}>{capitalizeTitle(product.title)}</h3>
                  <p style={styles.productPrice}>
                    {formatPrice(product.price, product.currency)}
                  </p>
                  <p style={styles.productDate}>
                    Added {new Date(product.created_at).toLocaleDateString()}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div style={styles.emptyState}>
            <Package style={{ width: '48px', height: '48px', color: '#d4d4d4', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '16px', color: '#171717', marginBottom: '8px' }}>No products yet</h3>
            <p style={{ fontSize: '14px', color: '#71717a', marginBottom: '24px' }}>
              Start adding products to showcase your collection
            </p>
            <Link to="/brand-portal/products/new" style={styles.addButton}>
              <Plus style={{ width: '18px', height: '18px' }} />
              Add Your First Product
            </Link>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      {hasProducts && (
        <div className="bp-quick-actions" style={styles.quickActions}>
          <Link to="/brand-portal/products/new" style={styles.actionCard}>
            <Plus style={{ width: '24px', height: '24px' }} />
            <span>Add Product</span>
          </Link>
          <Link to="/brand-portal/analytics" style={styles.actionCard}>
            <TrendingUp style={{ width: '24px', height: '24px' }} />
            <span>View Analytics</span>
          </Link>
          <Link to="/brand-portal/profile" style={styles.actionCard}>
            <Package style={{ width: '24px', height: '24px' }} />
            <span>Update Profile</span>
          </Link>
        </div>
      )}
    </div>
  );
}

const styles = {
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '400px',
    gap: '16px',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '2px solid #e5e5e5',
    borderTopColor: '#171717',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  header: {
    marginBottom: '40px',
  },
  label: {
    fontSize: '12px',
    letterSpacing: '0.3em',
    color: '#a1a1aa',
    marginBottom: '8px',
  },
  title: {
    fontSize: '42px',
    fontWeight: '400',
    color: '#171717',
    margin: '0 0 8px 0',
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: '15px',
    color: '#71717a',
    margin: '16px 0 0 0',
    lineHeight: 1.5,
  },
  error: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#ef4444',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '24px',
    fontSize: '14px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: '20px',
    marginBottom: '48px',
  },
  statCard: {
    background: '#ffffff',
    border: '1px solid #e5e5e5',
    borderRadius: '12px',
    padding: '24px',
    transition: 'all 0.2s',
    minHeight: '160px',
    display: 'flex',
    flexDirection: 'column',
  },
  statHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
  },
  statIcon: {
    width: '44px',
    height: '44px',
    border: '1px solid #e5e5e5',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statTrend: {
    display: 'flex',
    alignItems: 'center',
  },
  statValue: {
    fontSize: '36px',
    fontWeight: '400',
    color: '#171717',
    marginBottom: '4px',
    letterSpacing: '-0.02em',
  },
  statLabel: {
    fontSize: '11px',
    letterSpacing: '0.1em',
    color: '#a1a1aa',
    fontWeight: '500',
    marginBottom: '8px',
  },
  statDescription: {
    fontSize: '13px',
    color: '#71717a',
    marginTop: 'auto',
  },
  section: {
    marginTop: '0',
    marginBottom: '48px',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  sectionTitle: {
    fontSize: '28px',
    fontWeight: '400',
    color: '#171717',
    margin: 0,
    letterSpacing: '-0.01em',
  },
  viewAllLink: {
    fontSize: '14px',
    color: '#171717',
    fontWeight: '500',
    textDecoration: 'none',
  },
  productsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '20px',
  },
  productCard: {
    textDecoration: 'none',
    color: 'inherit',
    transition: 'transform 0.2s',
  },
  productImage: {
    aspectRatio: '3/4',
    borderRadius: '12px',
    overflow: 'hidden',
    background: '#f5f5f5',
    marginBottom: '12px',
  },
  productInfo: {
    padding: '0 4px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  productTitle: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#171717',
    margin: '0 0 6px 0',
    lineHeight: 1.4,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minHeight: '40px',
  },
  productPrice: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#171717',
    margin: '0 0 4px 0',
  },
  productDate: {
    fontSize: '12px',
    color: '#a1a1aa',
    margin: 0,
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    background: '#fafafa',
    borderRadius: '12px',
    border: '1px dashed #e5e5e5',
  },
  addButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '14px 24px',
    background: '#171717',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '500',
    textDecoration: 'none',
  },
  quickActions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginTop: '48px',
    paddingTop: '32px',
    borderTop: '1px solid #e5e5e5',
  },
  actionCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '20px 24px',
    background: '#fafafa',
    borderRadius: '12px',
    color: '#171717',
    fontSize: '15px',
    fontWeight: '500',
    textDecoration: 'none',
    transition: 'all 0.15s',
    border: '1px solid transparent',
  },
};

// Add responsive styles
const responsiveStyles = `
  @media (max-width: 1024px) {
    .bp-stats-grid {
      grid-template-columns: repeat(2, 1fr) !important;
    }
    .bp-catalog-stats {
      flex-direction: column !important;
    }
    .bp-catalog-stats > div {
      width: 100% !important;
    }
  }

  @media (max-width: 768px) {
    .bp-stats-grid {
      grid-template-columns: repeat(2, 1fr) !important;
      gap: 16px !important;
    }
    .bp-products-grid {
      grid-template-columns: repeat(2, 1fr) !important;
      gap: 16px !important;
    }
    .bp-quick-actions {
      grid-template-columns: 1fr !important;
    }
    .bp-catalog-stats {
      flex-direction: column !important;
    }
    .bp-catalog-stats > div {
      flex: none !important;
      width: 100% !important;
    }
  }

  @media (max-width: 480px) {
    .bp-stats-grid {
      grid-template-columns: 1fr !important;
      gap: 12px !important;
    }
    .bp-products-grid {
      grid-template-columns: repeat(2, 1fr) !important;
      gap: 12px !important;
    }
    .bp-quick-actions {
      grid-template-columns: 1fr !important;
    }
    .bp-empty-state {
      padding: 40px 16px !important;
    }
  }

  @media (max-width: 360px) {
    .bp-products-grid {
      grid-template-columns: 1fr !important;
    }
  }
`;

// Inject responsive styles
if (typeof document !== 'undefined' && !document.getElementById('brand-overview-responsive')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'brand-overview-responsive';
  styleSheet.textContent = responsiveStyles;
  document.head.appendChild(styleSheet);
}

export default BrandOverview;
export { BrandOverview };
